import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AiOutlineFileSearch,
  AiOutlineCheck,
  AiOutlineSetting,
  AiOutlineSafety,
  AiOutlineKey,
  AiOutlineBarChart,
  AiOutlineHistory,
  AiOutlineSync,
} from 'react-icons/ai';
import CommandPalette from './CommandPalette';
import useUser from '../../hooks/useUser';
import { isSessionExpired } from '../../auth/sessionExpiry';

/**
 * CommandPaletteProvider — monta el CommandPalette global con Cmd+K / Ctrl+K.
 *
 * Construye la lista de items desde los roles del usuario: solo incluye los
 * destinos a los que tiene acceso (mismas rutas que LandingPage).
 */
const CommandPaletteProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { hasRole, roles } = useUser();

  useEffect(() => {
    const onKey = (e) => {
      // Con la sesion vencida hay un aviso bloqueante arriba; este listener vive en `document` y
    // abriria la paleta ENCIMA de el, dando navegacion a una app que no puede operar (#171).
    if (isSessionExpired()) return;
    if ((e.metaKey || e.ctrlKey) && e.key?.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(true);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const items = useMemo(() => {
    if (!roles || roles.length === 0) return [];
    const go = (to) => () => navigate(to);
    const list = [];

    if (hasRole('VIEWER')) {
      list.push({ id: 'doctree', title: 'Árbol documental', subtitle: 'Navegar y buscar documentos', group: 'Navegación', icon: <AiOutlineFileSearch />, action: go('/doctree') });
    }
    if (hasRole('REVIEWER') || hasRole('APPROVER')) {
      list.push({ id: 'quality', title: 'Control de calidad', subtitle: 'Revisar / aprobar pendientes', group: 'Navegación', icon: <AiOutlineCheck />, action: go('/quality-control') });
    }
    if (hasRole('ADMIN')) {
      list.push(
        { id: 'specs', title: 'Series documentales', subtitle: 'Gestionar series y metadatos', group: 'Administración', icon: <AiOutlineSetting />, action: go('/admin/specs') },
        { id: 'acls', title: 'Gestión de ACLs', subtitle: 'Permisos de acceso', group: 'Administración', icon: <AiOutlineSafety />, action: go('/admin/acls') },
        { id: 'admin-keys', title: 'API Keys del sistema', subtitle: 'Claves de todos los usuarios/servicios', group: 'Administración', icon: <AiOutlineKey />, action: go('/admin/api-keys') },
        { id: 'retention', title: 'Políticas de retención', subtitle: 'Retención y disposición final', group: 'Administración', icon: <AiOutlineHistory />, action: go('/admin/retention-policies') },
        { id: 'reindex', title: 'Reindexar Elasticsearch', subtitle: 'Admin de índices', group: 'Administración', icon: <AiOutlineSync />, action: go('/admin/index') },
      );
    }
    if (hasRole('IMPORTER')) {
      list.push({ id: 'my-keys', title: 'Mis API keys', subtitle: 'Claves personales', group: 'Personal', icon: <AiOutlineKey />, action: go('/api-keys') });
    }
    list.push({ id: 'stats', title: 'Estadísticas de series', subtitle: 'Conteos por estado', group: 'Estadísticas', icon: <AiOutlineBarChart />, action: go('/admin/stats') });

    return list;
  }, [hasRole, roles, navigate]);

  return (
    <>
      {children}
      <CommandPalette open={open} onClose={() => setOpen(false)} items={items} />
    </>
  );
};

export default CommandPaletteProvider;
